import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT, STREET_SPAWNS, STREET_SPAWN_Y } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite, NATALKA_SIZE } from '../ui/NatalkaSprite.js';
import { reportLocation } from '../ui/locationTracker.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachSceneEditMode } from '../ui/SceneEditMode.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { fitCharacter } from '../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from './roomPerspective.mjs';
import { ySortDepth } from '../ui/ySort.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isInputLocked } from '../ui/inputLock.js';
import { showToast } from '../ui/Toast.js';
import { showDrivingSchoolMenu } from '../ui/DrivingSchoolMenu.js';
import { isNear } from '../ui/quizLogic.mjs';
import { FLOOR, OBSTACLES, EXIT_DOOR, SPAWN, NPC_NEAR, feetInAnyRect, rectContains } from './drivingSchoolLayout.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { renderBackground } from '../render/world.js';
import { iconText } from '../ui/iconText.js';

const HALF_H = NATALKA_SIZE.h / 2;
const ROOM = 'driving_school';
const INSTRUCTOR_ID = 'npc:instructor';
const ACTION_LABEL = 'Porozmawiaj {speak}';

export class DrivingSchoolScene extends Phaser.Scene {
  constructor() { super(SCENE.DRIVING_SCHOOL); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    this._spawn = { x: data?.spawnX ?? SPAWN.x, y: data?.spawnY ?? SPAWN.y };
    // Saved positions can land inside an obstacle after a geometry refinement — rescue
    // to the scene default instead of softlocking (WalkController has no unstick).
    if (feetInAnyRect(OBSTACLES, this._spawn.x, this._spawn.y + HALF_H)) this._spawn = { ...SPAWN };

    if (this.textures.exists('bg_driving_school')) {
      renderBackground(this, 'bg_driving_school');
    } else {
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xf3ead8);
      iconText(this, GAME_WIDTH / 2, 60, 'Szkoła nauki jazdy (brak tła)', { fontFamily: 'system-ui', fontSize: '28px', color: '#333' }).setOrigin(0.5);
    }

    this.perspective = ROOM_PERSPECTIVE.driving_school;   // smaller and slower towards the far edge
    this.natalka = new NatalkaSprite(this, this._spawn.x, this._spawn.y);
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.walker = new WalkController(this, this.natalka, {
      floorPolyline: FLOOR,
      blocked: (fx, fy) => feetInAnyRect(OBSTACLES, fx, fy),
      onBroadcast: (p) => realtime.move(p),
    });
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });

    new MoneyBar(this);
    attachFriendsButton(this);
    this._unsub = gameState.on(() => this.natalka?.setOutfit(gameState.raw?.inventory?.equipped));
    this.events.on('shutdown', () => this._unsub?.());
    attachCoordsDebug(this);
    if (import.meta.env?.DEV) attachSceneEditMode(this);  // press E: author floor/obstacles

    // The instructor is a server-driven NPC (backend catalog NPC_DEFS): she arrives through
    // the room roster, wanders on the server ticker, and is grabbed here so we can measure
    // proximity to her.
    this.instructor = null;
    this._presence = attachRemotePresence(this, ROOM, {
      onNpc: (id, avatar) => { if (id === INSTRUCTOR_ID) this.instructor = avatar; },
    });
    this._roomChat = attachRoomChat(this, ROOM);

    // Clicking the instructor = the action (same idiom as the Game Hall counter/chairs).
    this.input.on('pointerup', (p) => {
      if (isInputLocked(this) || !this.instructor) return;
      const s = this.instructor.sprite;
      if (Math.abs(p.worldX - s.x) < s.displayWidth / 2 && Math.abs(p.worldY - s.y) < s.displayHeight / 2) this._talk();
    });

    this._spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    // Don't re-trigger the exit door right after spawning next to it.
    this._zoneGuardUntil = Date.now() + 600;
    this._exitTriggered = false;
    this._talking = false;

    this.events.on('shutdown', () => {
      if (virtualInput.getAction()?.label === ACTION_LABEL) virtualInput.clearAction();
      if (this._talking) realtime.npcRelease(INSTRUCTOR_ID);
      this.instructor = null;
    });
  }

  // Hold the NPC (she stops wandering for everyone while we talk), open the menu,
  // release her when it closes.
  async _talk() {
    if (this._talking || isInputLocked(this)) return;
    if (!this.instructor || !isNear(this.natalka.x, this.natalka.y, this.instructor.sprite.x, this.instructor.sprite.y, NPC_NEAR)) {
      showToast(this, 'Podejdź do Pani Instruktor {car}');
      return;
    }
    this._talking = true;
    realtime.npcInteract(INSTRUCTOR_ID);
    try {
      await showDrivingSchoolMenu(this);
    } finally {
      realtime.npcRelease(INSTRUCTOR_ID);
      this._talking = false;
    }
  }

  update(_t, dt) {
    this.walker.update(dt);
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y);
    this._depthSort();
    const fx = this.natalka.x, fy = this.natalka.feetY;

    const near = !!this.instructor && isNear(this.natalka.x, this.natalka.y, this.instructor.sprite.x, this.instructor.sprite.y, NPC_NEAR);
    if (isInputLocked(this)) {
      if (virtualInput.getAction()?.label === ACTION_LABEL) virtualInput.clearAction();
    } else if (near && virtualInput.getAction()?.label !== ACTION_LABEL) {
      virtualInput.registerAction(ACTION_LABEL, () => this._talk());
    } else if (!near && virtualInput.getAction()?.label === ACTION_LABEL) {
      virtualInput.clearAction();
    }
    if (Phaser.Input.Keyboard.JustDown(this._spaceKey) && !isInputLocked(this)
        && virtualInput.getAction()?.label === ACTION_LABEL) {
      virtualInput.invokeAction();
    }

    if (this._exitTriggered || Date.now() < this._zoneGuardUntil) return;
    if (rectContains(EXIT_DOOR, fx, fy)) {
      this._exitTriggered = true;
      this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.DRIVING_SCHOOL, spawnY: STREET_SPAWN_Y, upExit: this.walker.walkingUp });
    }
  }

  _depthSort() {
    this.natalka.setDepth(ySortDepth(this.natalka.feetY));
    if (this._presence) for (const a of this._presence.avatars.values()) a.sprite.setDepth(ySortDepth((a.sprite.feetY ?? a.sprite.y + HALF_H)));
  }
}
